import {Injectable} from '@angular/core';
import {ApiVersion} from '../constants/apis';
import {AbstractService} from './abstract.service';
import {ErrorFileList} from '../models/error-file-list';
import {BlobUploadInfo} from '../models/gcs-blob.model';
import {UserService} from './user.service';
import {environment} from '../../environments/environment';
import {HttpClient, HttpHeaders, HttpParams} from '@angular/common/http';
import {from, Observable} from 'rxjs';
import {switchMap} from 'rxjs/operators';
import {UploadErrorCallback} from '../models/upload-error-callback.model';
import {ErrorFile} from '../models/error-file';
import {AuthService} from './auth.service';

@Injectable({
  providedIn: 'root'
})
export class UploadErrorsService extends AbstractService {

  SLASH = '/';
  OCTET_STREAM_CONTENT_TYPE = 'application/octet-stream';
  GCS_API = environment.gcsAPI;
  callbackData: UploadErrorCallback;

  constructor(private http: HttpClient, private userService: UserService, private authService: AuthService) {
    super();
  }

  getCallbackData(): UploadErrorCallback {
    return this.callbackData;
  }

  setCallbackData(uploadErrorCallback: UploadErrorCallback): void {
    this.callbackData = uploadErrorCallback;
  }

  clearCallbackData(): void {
    this.callbackData = null;
  }

  list(pageNumber: number): Observable<ErrorFileList> {
    const url = this.toFinraApiServiceUrl(ApiVersion.V1, 'files/manual');

    const params = new HttpParams()
      .set('pageNumber', String(pageNumber));

    return this.http.get<ErrorFileList>(url, {params});
  }

  getUploadErrorBucket(): Observable<{ bucketName: string }> {
    const url = this.toFinraApiServiceUrl(ApiVersion.V1, 'files/manual/upload-error-bucket');
    return this.http.get<{ bucketName: string }>(url);
  }

  copy(blobUploadInfo: BlobUploadInfo, errorFile: ErrorFile): Observable<void> {
    const params = {blobUploadInfo, fileError: errorFile};
    const url = this.toFinraApiServiceUrl(ApiVersion.V1, 'files/manual');
    return this.http.post<void>(url, params);
  }

  insertObject(bucket: string, event: Event): Observable<any> {
    const fileData = (event.target as HTMLInputElement).files[0];
    const contentType = fileData.type || this.OCTET_STREAM_CONTENT_TYPE;
    const objectPrefix = this.constructObjectPrefix();
    const metadata = {name: objectPrefix + fileData.name, mimeType: contentType};
    const path = this.GCS_API.replace('{BUCKET}', bucket);
    const boundary = '-------314159265358979323846';

    const readFile$ = from(
      fileData.arrayBuffer().then(buffer => {
        const bytes = new Uint8Array(buffer);
        const CHUNK = 3 * 1024;
        const parts: string[] = [];
        for (let i = 0; i < bytes.length; i += CHUNK) {
          let s = '';
          const end = Math.min(i + CHUNK, bytes.length);
          for (let j = i; j < end; j++) { s += String.fromCharCode(bytes[j]); }
          parts.push(btoa(s));
        }
        return parts.join('');
      })
    );

    return readFile$.pipe(
      switchMap(base64Data => {
        const gcsToken = this.authService.getGcsToken();
        const delimiter = '\r\n--' + boundary + '\r\n';
        const body =
          delimiter + 'Content-Type: application/json\r\n\r\n' + JSON.stringify(metadata) +
          delimiter + 'Content-Type: ' + contentType + '\r\n' + 'Content-Transfer-Encoding: base64\r\n\r\n' +
          base64Data + '\r\n--' + boundary + '--';
        let headers = new HttpHeaders({'Content-Type': 'multipart/mixed; boundary="' + boundary + '"'});
        if (gcsToken) {
          headers = headers.set('Authorization', `Bearer ${gcsToken}`);
        }
        const params = new HttpParams().set('uploadType', 'multipart');
        return this.http.post(path, body, {headers, params});
      })
    );
  }

  constructObjectPrefix(): string {
    return this.userService.user.domain + this.SLASH + this.userService.user.email + this.SLASH + new Date().getTime() + this.SLASH;
  }
}
